/**
 * 「+ 書類作成」クイックアクション (Phase A / P-E + P-H ボーナス B 構造分離)。
 *
 * メニューを 3 セクションに分割:
 *  1. アクション: 見積書 (手動作成)
 *  2. 自動起票: 注文請書 / 納品書 / 請求書 (クリックで fulfillment タブへ案内)
 *  3. 準備中: 同意書 / 契約書 (クリック不可・グレー表示)
 *
 * 「クリックして実際に動くもの」と「ステータス確認」と「未実装」が視覚的に区別され、
 * ユーザーが「どれを押せば見積書を作れるか」を直感的に判断できる。
 */
import {
  FilePlus2,
  FileText,
  Receipt,
  ScrollText,
  ShieldCheck,
  ShoppingCart,
  Truck,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';

interface Props {
  /** どの商談に紐付けるか。null のときはボタンを disabled にする。 */
  dealId: string | null;
  /** 見積書クリック時。CreateQuoteDialog を開く想定。 */
  onCreateQuote?: () => void;
  /** 注文請書 / 納品書 / 請求書クリック時。fulfillment タブにジャンプ。 */
  onJumpFulfillment?: () => void;
}

export function DocumentQuickActions({ dealId, onCreateQuote, onJumpFulfillment }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <Button variant="primary" size="sm" disabled={!dealId} onClick={() => setOpen((v) => !v)}>
        <FilePlus2 className="size-3.5" />+ 書類作成
      </Button>
      {open && dealId && (
        <div
          className="absolute right-0 top-full mt-1 z-10 w-72 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] shadow-lg p-1.5"
          onMouseLeave={() => setOpen(false)}
        >
          {/* 1. 手動作成 (押せば動く) */}
          <SectionLabel>手動作成</SectionLabel>
          <ActionRow
            icon={FileText}
            label="見積書を作成"
            description="商品マスターから商品を選んで見積書を発行"
            color="var(--accent)"
            onClick={() => {
              setOpen(false);
              if (onCreateQuote) {
                onCreateQuote();
              } else {
                toast.info('見積作成は商談詳細または顧客詳細の「商談」タブから行ってください');
              }
            }}
          />

          {/* 2. 自動起票 (情報のみ) */}
          <SectionLabel>自動起票 (確認のみ)</SectionLabel>
          <InfoRow
            icon={ShoppingCart}
            label="注文請書"
            description="入金確認時に自動起票されます"
            onClick={() => {
              setOpen(false);
              toast.info('入金確認時に自動起票されます');
              onJumpFulfillment?.();
            }}
          />
          <InfoRow
            icon={Truck}
            label="納品書"
            description="発注の「顧客へ発送」で発送が作られ、「納品完了」で登録されます"
            onClick={() => {
              setOpen(false);
              toast.info('発注の「顧客へ発送」で発送が作られ、「納品完了」で登録されます');
              onJumpFulfillment?.();
            }}
          />
          <InfoRow
            icon={Receipt}
            label="請求書"
            description="成約確定 (markDealWon) で自動起票されます"
            onClick={() => {
              setOpen(false);
              toast.info('成約確定 (markDealWon) で自動起票されます');
              onJumpFulfillment?.();
            }}
          />

          {/* 3. 準備中 (押せない) */}
          <SectionLabel>Phase 10 で対応予定</SectionLabel>
          <DisabledRow icon={ShieldCheck} label="同意書" />
          <DisabledRow icon={ScrollText} label="契約書" />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// セクション内部
// ─────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
      {children}
    </div>
  );
}

function ActionRow({
  icon: Icon,
  label,
  description,
  color,
  onClick,
}: {
  icon: typeof FileText;
  label: string;
  description: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start gap-2 w-full text-left px-3 py-2 rounded-sm hover:bg-[var(--bg-hover)]"
    >
      <Icon className="size-4 mt-0.5 flex-shrink-0" style={{ color }} />
      <div className="min-w-0">
        <div className="text-sm font-medium text-[var(--text-primary)]">{label}</div>
        <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{description}</div>
      </div>
    </button>
  );
}

function InfoRow({
  icon: Icon,
  label,
  description,
  onClick,
}: {
  icon: typeof FileText;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start gap-2 w-full text-left px-3 py-2 rounded-sm hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]"
    >
      <Icon className="size-3.5 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-xs">{label}</div>
        <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{description}</div>
      </div>
    </button>
  );
}

function DisabledRow({ icon: Icon, label }: { icon: typeof FileText; label: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-[var(--text-muted)] opacity-60">
      <Icon className="size-3.5" />
      {label}
    </div>
  );
}
